import React, { useState, useEffect } from "react";
import KpiCard from "../components/Dashboard/KpiCard";
import DonutChart from "../components/Charts/DonutChart";
import BarChart from "../components/Charts/BarChart";
import ProgressBar from "../components/Dashboard/ProgressBar";
import { get, post } from "../utils/api";

// ── Lucide icons ────────────────────────────────────────────────────────────
import {
    ClipboardCheck,
    Activity,
    Users,
    FileText,
    Search,
    CheckCircle2,
    Clock,
    XCircle
} from "lucide-react";

// ── Components ──────────────────────────────────────────────────────────────

const OpsTopDistributorItem = ({ rank, name, arn, transactions, volume, status }) => (
    <div className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl mb-3 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-sm">
                #{rank}
            </div>
            <div>
                <h4 className="font-bold text-gray-900 text-sm leading-tight">{name}</h4>
                <p className="text-xs text-gray-500">{arn} • {transactions} transactions</p>
            </div>
        </div>
        <div className="text-right">
            <div className="text-sm font-bold text-green-600">₹{volume} Cr</div>
            <span className="text-[10px] px-2 py-0.5 bg-green-50 text-green-600 rounded-full font-medium">
                {status}
            </span>
        </div>
    </div>
);

// ── Constants / Sample Data ──────────────────────────────────────────────────

const KYC_TREND_DATA = {
    labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
    datasets: [
        {
            label: "Approved",
            data: [35, 38, 38, 42, 45, 52],
            borderColor: "#10B981",
            backgroundColor: "rgba(16,185,129,0.1)",
            fill: true,
            tension: 0.4,
        },
        {
            label: "Pending",
            data: [42, 45, 45, 50, 48, 55],
            borderColor: "#F59E0B",
            backgroundColor: "rgba(245,158,11,0.05)",
            fill: true,
            tension: 0.4,
        },
        {
            label: "Rejected",
            data: [45, 48, 48, 55, 52, 62],
            borderColor: "#EF4444",
            backgroundColor: "rgba(239,68,68,0.05)",
            fill: true,
            tension: 0.4,
        }
    ]
};


const DISTRIBUTOR_STATUS_DATA = {
    labels: ["Approved", "Pending Approval", "Rejected"],
    datasets: [
        {
            label: "Distributors",
            data: [45, 18, 5],
            backgroundColor: ["#10B981", "#F59E0B", "#EF4444"],
            borderRadius: 4,
            barThickness: 8,
        }
    ]
};


const RECENT_ACTIVITIES = [
    { action: "KYC Approved", user: "Raman Kumar", details: "Digital Onboarding", module: "AMC Listing", time: "1 hour ago", status: "Completed" },
    { action: "Agreement Signed", user: "HDFC WEALTH INDIA PVT LTD", details: "Distributor Agreement", module: "Distributor Listing", time: "3 hours ago", status: "Completed" },
    { action: "Transactions Uploaded", user: "Batch upload", details: "150 transactions", module: "Transaction Details", time: "5 hours ago", status: "Completed" },
    { action: "Invoices Generated", user: "INV-OPS-2023-102", details: "For NFL/038", module: "Invoice", time: "1 day ago", status: "Completed" },
    { action: "KYC Pending Review", user: "Priya Sharma", details: "Documentation Incomplete", module: "AMC Listing", time: "1 day ago", status: "Pending" },
];

const OpsOverviewDashboard = () => {
    const [loading, setLoading] = useState(false);
    const [kpiData, setKpiData] = useState({
        pendingKyc: "...",
        totalTransactions: "...",
        approvedDistributors: "...",
        invoicesMtd: "..."
    });

    const [invoiceTrend, setInvoiceTrend] = useState({
        labels: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
        datasets: [
            { label: "Generated", data: [0, 0, 0, 0, 0, 0], backgroundColor: "#6366F1" },
            { label: "Paid", data: [0, 0, 0, 0, 0, 0], backgroundColor: "#10B981" }
        ]
    });

    const [distributorStatus, setDistributorStatus] = useState({
        labels: ["Approved", "Pending", "Rejected"],
        data: [0, 0, 0],
        total: 0
    });

    const [kycDocCompletion, setKycDocCompletion] = useState({
        documents: [],
        avgCompletion: 0
    });

    const [topDistributors, setTopDistributors] = useState([]);

    const [transactionStatus, setTransactionStatus] = useState({
        labels: ["Processed", "Pending", "Failed"],
        data: [0, 0, 0],
        total: 0,
        colors: ["#10B981", "#FBA919", "#EF4444"],
        legendItems: []
    });


    const formatTimestamp = () => {
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        const formattedTime = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
        return `${formattedDate} – ${formattedTime}`;
    };

    const getPreviousMonthYear = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    };

    const monthInfo = getPreviousMonthYear();

    const [lastUpdated, setLastUpdated] = useState(formatTimestamp());

    useEffect(() => {
        const fetchOpsData = async () => {
            setLoading(true);
            try {
                const [kycRes, distRes, invoiceStatusRes, transactionRes, distApprovalRes, transStatusRes, invoiceTrendRes, kycDocCompletionRes, topDistRes] = await Promise.all([
                    get("/admindashboard/Get_KYC_Pending_Count"),
                    get("/admindashboard/GetTotalDistributor_Details_Count"),
                    get("/admindashboard/Get_Invoice_Status_Count"),
                    post("/admindashboard/Get_MonthLevel_ProductSales_Count", {}),
                    get("/admindashboard/GetDistributorApprovalStatus_Count"),
                    get("/admindashboard/GetTransactionStatus_Count"),
                    get("/admindashboard/Get_Invoice_Trend_MonthWise"),
                    get("/admindashboard/Get_KYC_Document_Completion"),
                    get("/admindashboard/Get_Top_Distributors_Ops")
                ]);

                // 1. Pending KYC
                let pendingKycCount = 0;
                if (kycRes.data.Success && kycRes.data.Data.length > 0) {
                    pendingKycCount = kycRes.data.Data[0].pending_count || 0;
                }

                // 2. Approved Distributors
                let approvedDistCount = 0;
                if (distRes.data.Success && distRes.data.Data.length > 0) {
                    approvedDistCount = distRes.data.Data[0].total_distributor_details_count || 0;
                }

                // 3. Invoice MTD (Total count of all invoice statuses)
                let invoicesMtdCount = 0;
                if (invoiceStatusRes.data.Success && invoiceStatusRes.data.Data.length > 0) {
                    const statusData = invoiceStatusRes.data.Data[0];
                    invoicesMtdCount = parseInt(statusData.APPROVED || 0) + parseInt(statusData.PENDING_FOR_APPROVAL || 0);
                }

                // 4. Total Transactions (Sum of td_count for current month)
                let totalTransCount = 0;
                if (transactionRes.data.Success && Array.isArray(transactionRes.data.Data)) {
                    totalTransCount = transactionRes.data.Data.reduce((acc, curr) => acc + parseInt(curr.td_count || 0), 0);
                }

                // 5. Distributor Approval Status Mapping
                if (distApprovalRes.data.Success && Array.isArray(distApprovalRes.data.Data)) {
                    const apiData = distApprovalRes.data.Data;
                    const labels = apiData.map(item => item.status_desc);
                    const counts = apiData.map(item => parseInt(item.status_count || 0));
                    const totalDist = counts.reduce((a, b) => a + b, 0);
                    setDistributorStatus({ labels, data: counts, total: totalDist });
                }

                // 6. Transaction Status Mapping
                if (transStatusRes.data.Success && Array.isArray(transStatusRes.data.Data)) {
                    const apiData = transStatusRes.data.Data;
                    const labels = apiData.map(item => item.status_desc);
                    const counts = apiData.map(item => parseInt(item.status_count || 0));
                    const totalTrans = counts.reduce((a, b) => a + b, 0);

                    // Map colors based on status name (simple heuristic)
                    const colors = labels.map(label => {
                        const lowLabel = label.toLowerCase();
                        if (lowLabel.includes('processed') || lowLabel.includes('success') || lowLabel.includes('approved')) return "#10B981";
                        if (lowLabel.includes('pending') || lowLabel.includes('wip') || lowLabel.includes('submitted')) return "#FBA919";
                        if (lowLabel.includes('failed') || lowLabel.includes('error') || lowLabel.includes('rejected')) return "#EF4444";
                        return "#6366F1"; // Default purple
                    });

                    const legendItems = apiData.map((item, i) => ({
                        label: item.status_desc,
                        value: parseInt(item.status_count || 0).toLocaleString(),
                        color: colors[i]
                    }));

                    setTransactionStatus({
                        labels,
                        data: counts,
                        total: totalTrans,
                        colors,
                        legendItems
                    });
                }

                // 7. Invoice Trend Mapping
                if (invoiceTrendRes && invoiceTrendRes.data.Success && Array.isArray(invoiceTrendRes.data.Data)) {
                    const apiData = invoiceTrendRes.data.Data;
                    setInvoiceTrend({
                        labels: apiData.map(item => item.label),
                        datasets: [
                            {
                                label: "Generated",
                                data: apiData.map(item => parseInt(item.generated_count || 0)),
                                backgroundColor: "#6366F1",
                            },
                            {
                                label: "Paid",
                                data: apiData.map(item => parseInt(item.paid_count || 0)),
                                backgroundColor: "#10B981",
                            }
                        ]
                    });
                }
                // 8. KYC Document Completion
                if (kycDocCompletionRes?.data?.Success && Array.isArray(kycDocCompletionRes.data.Data) && kycDocCompletionRes.data.Data.length > 0) {
                    const docs = kycDocCompletionRes.data.Data;
                    const totalPct = docs.reduce((sum, d) => sum + parseFloat(d.completion_percentage || 0), 0);
                    const avg = docs.length > 0 ? Math.round(totalPct / docs.length) : 0;
                    setKycDocCompletion({
                        documents: docs,
                        avgCompletion: avg
                    });
                }

                // 9. Top Distributors
                if (topDistRes?.data?.Success && Array.isArray(topDistRes.data.Data)) {
                    setTopDistributors(topDistRes.data.Data);
                }

                setKpiData({
                    pendingKyc: pendingKycCount.toLocaleString(),
                    totalTransactions: totalTransCount.toLocaleString(),
                    approvedDistributors: approvedDistCount.toLocaleString(),
                    invoicesMtd: invoicesMtdCount.toLocaleString()
                });

                setLastUpdated(formatTimestamp());
            } catch (error) {
                console.error("Error fetching Ops dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOpsData();
    }, []);

    return (
        <div className="ops-overview p-6 space-y-6 max-w-[1600px] mx-auto">
            {/* Header */}
            <div className="flex justify-between items-end">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Operations Dashboard</h1>
                    <p className="text-gray-500 text-sm">Monitor KYC, distributor approvals, transactions, and invoices</p>
                </div>
                <span className="text-xs text-gray-400 font-medium pb-1">
                    Last updated: <strong>{lastUpdated}</strong>
                </span>
            </div>

            {/* KPI Cards */}
            <div className="admin-kpi-grid">
                <KpiCard
                    title="Pending KYC"
                    value={kpiData.pendingKyc}
                    subtitle="Requires review"
                    trend={-12}
                    icon={<Clock size={20} />}
                    iconBgColor="#FFFBEB"
                    iconColor="#F59E0B"
                />
                <KpiCard
                    title="Total Transactions"
                    value={kpiData.totalTransactions}
                    subtitle="This month (est)"
                    trend={18}
                    icon={<Activity size={20} />}
                    iconBgColor="#EEF2FF"
                    iconColor="#6366F1"
                />
                <KpiCard
                    title="Approved Distributors"
                    value={kpiData.approvedDistributors}
                    subtitle="signed agreements"
                    trend={5}
                    icon={<Users size={20} />}
                    iconBgColor="#ECFDF5"
                    iconColor="#10B981"
                />
                <KpiCard
                    title="Invoices (MTD)"
                    value={kpiData.invoicesMtd}
                    subtitle="Awaiting payment"
                    trend={14}
                    icon={<FileText size={20} />}
                    iconBgColor="#F5F3FF"
                    iconColor="#8B5CF6"
                />
            </div>

            {/* Row 1: Transaction Status + Distributor Approval Status */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="dash-card">
                    <div className="dash-card-header flex justify-between items-start mb-8">
                        <div>
                            <h3 className="dash-section-title">Transaction Status</h3>
                            <p className="dash-section-sub">Current activity cycle</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                             <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                                {monthInfo}
                            </span>
                            <div className="text-right">
                                <span className="text-xs font-semibold text-gray-400 block">Total Activities</span>
                                <span className="text-sm font-bold text-gray-900">{transactionStatus.total.toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center py-2">
                        <DonutChart
                            labels={transactionStatus.labels}
                            data={transactionStatus.data}
                            colors={transactionStatus.colors}
                            centerLabel={transactionStatus.total.toLocaleString()}
                            height={250}
                            legendItems={transactionStatus.legendItems}
                        />
                    </div>
                </div>
                <div className="dash-card">
                    <div className="dash-card-header mb-8">
                        <div>
                            <h3 className="dash-section-title">Distributor Approval Status</h3>
                            <p className="dash-section-sub">Pipeline throughput</p>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-semibold text-gray-400 block">Total Base</span>
                            <span className="text-sm font-bold text-gray-900">{distributorStatus.total} Distributors</span>
                        </div>
                    </div>
                    <div className="space-y-8 mt-2 px-2">
                        {distributorStatus.labels.map((label, i) => (
                            <ProgressBar
                                key={label}
                                label={label}
                                value={distributorStatus.data[i]}
                                total={distributorStatus.total}
                                color={label.toLowerCase().includes('approved') ? "#10B981" : label.toLowerCase().includes('rejected') ? "#EF4444" : "#FBA919"}
                                size="sm"
                            />
                        ))}
                    </div>
                </div>
            </div>
            {/* Row 2: Invoice Trend + KYC Document Completion */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="dash-card">
                    <div className="dash-card-header flex justify-between items-start mb-4">
                        <div>
                            <h3 className="dash-section-title">Invoice Trend</h3>
                            <p className="dash-section-sub">Monthly invoice generation & payment</p>
                        </div>
                        <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                            {monthInfo}
                        </span>
                    </div>
                    <BarChart
                        labels={invoiceTrend.labels}
                        datasets={invoiceTrend.datasets}
                        height={250}
                    />
                </div>
                <div className="dash-card">
                    <div className="dash-card-header mb-8">
                        <div>
                            <h3 className="dash-section-title">KYC Document Completion</h3>
                            <p className="dash-section-sub">Digital verification status</p>
                        </div>
                        <div className="text-right">
                            <span className="text-xs font-semibold text-gray-400 block">Avg. Completion</span>
                            <span className="text-sm font-bold text-green-600">{kycDocCompletion.avgCompletion}%</span>
                        </div>
                    </div>
                    <div className="space-y-6 mt-2 px-2 max-h-64 overflow-y-auto">
                        {kycDocCompletion.documents.length > 0 ? (
                            kycDocCompletion.documents.map((doc, i) => {
                                const colors = ["#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#6366F1", "#EF4444", "#14B8A6", "#F97316", "#84CC16", "#06B6D4"];
                                return (
                                    <ProgressBar
                                        key={doc.document_id}
                                        label={doc.document_name}
                                        percentage={parseFloat(doc.completion_percentage || 0)}
                                        color={colors[i % colors.length]}
                                    />
                                );
                            })
                        ) : (
                            <div className="text-center text-gray-400 text-sm py-8">No KYC document data available</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Row 3: Top Distributors */}
            <div className="dash-card">
                <div className="dash-card-header flex justify-between items-start mb-6">
                    <div>
                        <h3 className="dash-section-title">Top Distributors</h3>
                        <p className="dash-section-sub">By transaction count</p>
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
                        {monthInfo}
                    </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
                    {topDistributors.length > 0 ? (
                        topDistributors.map((dist, i) => (
                            <OpsTopDistributorItem
                                key={dist.arn_code + i}
                                rank={i + 1}
                                name={dist.distributor_name}
                                arn={dist.arn_code}
                                transactions={parseInt(dist.transaction_count || 0).toLocaleString()}
                                volume={parseFloat(dist.volume_in_cr || 0).toFixed(2)}
                                status={dist.status}
                            />
                        ))
                    ) : (
                        <div className="col-span-2 text-center text-gray-400 text-sm py-8">No distributor data available</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OpsOverviewDashboard;
